import { useState, useEffect, useCallback } from 'react';
import { 
  Search, Eye, Trash2, Mail, Phone, MoreVertical, Download, 
  Calendar, Filter, ChevronLeft, ChevronRight, Send, X, 
  CheckCircle, Clock, UserCheck, XCircle, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import { adminApi } from '../../lib/api';
import { formatDate, cn } from '../../lib/utils';
import { toast } from 'sonner';

const statusConfig = {
  pending: { 
    label: 'Pending', 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Clock
  },
  contacted: { 
    label: 'Contacted', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Phone
  },
  converted: { 
    label: 'Converted', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle
  },
  closed: { 
    label: 'Closed', 
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: XCircle
  },
};

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusSummary, setStatusSummary] = useState({});
  const limit = 15;

  // Modal state
  const [modalStatus, setModalStatus] = useState('');
  const [modalNotes, setModalNotes] = useState('');
  const [emailResponse, setEmailResponse] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await adminApi.getEnquiries(params);
      const data = response.data;
      
      setEnquiries(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setStatusSummary(data.status_summary || {});
    } catch (error) {
      console.error('Error loading enquiries:', error);
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchTerm, dateFrom, dateTo]);

  const handleStatusChange = async (id, status) => {
    try {
      await adminApi.updateEnquiryStatus(id, { status });
      toast.success(`Status updated to ${statusConfig[status].label}`);
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await adminApi.deleteEnquiry(id);
      toast.success('Enquiry deleted successfully');
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected enquiries?`)) return;
    
    try {
      await adminApi.bulkDelete(selectedIds);
      toast.success(`${selectedIds.length} enquiries deleted`);
      setSelectedIds([]);
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiries');
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedIds.length === 0) return;
    
    try {
      await adminApi.bulkUpdateStatus(selectedIds, status);
      toast.success(`${selectedIds.length} enquiries updated to ${statusConfig[status].label}`);
      setSelectedIds([]);
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = { format: 'csv' };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await adminApi.exportEnquiries(params);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enquiries_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Enquiries exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export enquiries');
    } finally {
      setExporting(false);
    }
  };

  const openDetailModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setModalStatus(enquiry.status);
    setModalNotes('');
    setEmailResponse('');
    setShowDetailModal(true);
  };

  const handleModalStatusUpdate = async () => {
    if (!selectedEnquiry) return;
    setUpdatingStatus(true);
    
    try {
      await adminApi.updateEnquiryStatus(selectedEnquiry.id, {
        status: modalStatus,
        notes: modalNotes || undefined
      });
      toast.success('Enquiry updated successfully');
      setShowDetailModal(false);
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to update enquiry');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedEnquiry || !emailResponse.trim()) {
      toast.error('Please enter an email response');
      return;
    }
    
    // Open email client with pre-filled response
    const subject = encodeURIComponent(`Re: Your Enquiry - NYRAV Tours`);
    const body = encodeURIComponent(emailResponse);
    window.location.href = `mailto:${selectedEnquiry.email}?subject=${subject}&body=${body}`;
    toast.success('Email client opened');
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === enquiries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(enquiries.map(e => e.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div data-testid="admin-enquiries" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Enquiries
          </h1>
          <p className="text-[#4A4A4A]">
            Manage customer enquiries • {total} total
          </p>
        </div>
        <Button 
          onClick={handleExportCSV} 
          variant="outline"
          disabled={exporting}
          data-testid="export-csv-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => {
          const Icon = config.icon;
          const count = statusSummary[key] || 0;
          const isActive = statusFilter === key;
          
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(isActive ? 'all' : key)}
              className={cn(
                'p-4 rounded-xl border-2 transition-all text-left',
                isActive 
                  ? 'border-[#FF6B35] bg-[#FF6B35]/5' 
                  : 'border-gray-100 bg-white hover:border-gray-200'
              )}
              data-testid={`status-filter-${key}`}
            >
              <div className="flex items-center justify-between">
                <div className={cn('p-2 rounded-lg', config.color)}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-2xl font-bold text-[#004E64]">{count}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-[#4A4A4A]">{config.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input 
              placeholder="Search by name, email, or phone..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="date"
                placeholder="From"
                className="pl-10 w-40"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from"
              />
            </div>
            <span className="text-gray-400">to</span>
            <Input
              type="date"
              className="w-40"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="date-to"
            />
          </div>

          {(searchTerm || statusFilter !== 'all' || dateFrom || dateTo) && (
            <Button variant="ghost" onClick={clearFilters} className="text-[#FF6B35]">
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bg-[#004E64] text-white rounded-xl p-4 flex items-center justify-between">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary">
                  Update Status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <DropdownMenuItem key={key} onClick={() => handleBulkStatusUpdate(key)}>
                    {config.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-12">
                <input
                  type="checkbox"
                  checked={selectedIds.length === enquiries.length && enquiries.length > 0}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300"
                />
              </TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF6B35]" />
                  <p className="mt-2 text-[#4A4A4A]">Loading enquiries...</p>
                </TableCell>
              </TableRow>
            ) : enquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-[#4A4A4A]">No enquiries found</p>
                  {(searchTerm || statusFilter !== 'all' || dateFrom || dateTo) && (
                    <Button variant="link" onClick={clearFilters} className="mt-2">
                      Clear filters
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              enquiries.map((enquiry) => {
                const status = statusConfig[enquiry.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                
                return (
                  <TableRow 
                    key={enquiry.id} 
                    className="hover:bg-gray-50"
                    data-testid={`enquiry-row-${enquiry.id}`}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(enquiry.id)}
                        onChange={() => toggleSelect(enquiry.id)}
                        className="rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell className="text-sm text-[#4A4A4A]">
                      {formatDate(enquiry.created_at)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-[#1A1A1A]">{enquiry.name}</p>
                        <p className="text-sm text-[#4A4A4A]">{enquiry.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <a href={`tel:${enquiry.phone}`} className="text-[#004E64] hover:underline">
                        {enquiry.phone}
                      </a>
                    </TableCell>
                    <TableCell className="text-sm">
                      {enquiry.package_name || (
                        <span className="text-gray-400 italic">General Enquiry</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select 
                        value={enquiry.status} 
                        onValueChange={(value) => handleStatusChange(enquiry.id, value)}
                      >
                        <SelectTrigger className={cn('w-32 h-8 text-xs font-medium border', status.color)}>
                          <div className="flex items-center gap-1.5">
                            <StatusIcon className="w-3 h-3" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <config.icon className="w-3 h-3" />
                                {config.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`actions-${enquiry.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetailModal(enquiry)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.location.href = `mailto:${enquiry.email}`}>
                            <Mail className="w-4 h-4 mr-2" /> Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.location.href = `tel:${enquiry.phone}`}>
                            <Phone className="w-4 h-4 mr-2" /> Call
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(enquiry.id)} 
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-[#4A4A4A]">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-[#4A4A4A]">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Enquiry Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEnquiry && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase">Name</label>
                  <p className="font-semibold text-[#1A1A1A]">{selectedEnquiry.name}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase">Email</label>
                  <a href={`mailto:${selectedEnquiry.email}`} className="block font-semibold text-[#FF6B35] hover:underline">
                    {selectedEnquiry.email}
                  </a>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase">Phone</label>
                  <a href={`tel:${selectedEnquiry.phone}`} className="block font-semibold text-[#004E64] hover:underline">
                    {selectedEnquiry.phone}
                  </a>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase">Date</label>
                  <p className="font-semibold">{formatDate(selectedEnquiry.created_at)}</p>
                </div>
              </div>

              {/* Package & Travel Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase">Package</label>
                    <p className="font-semibold">{selectedEnquiry.package_name || 'General Enquiry'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase">Travel Date</label>
                    <p className="font-semibold">{selectedEnquiry.travel_date || 'Not specified'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase">Travelers</label>
                    <p className="font-semibold">
                      {selectedEnquiry.number_of_travelers?.adults || 1} Adults, {selectedEnquiry.number_of_travelers?.children || 0} Children
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-500 uppercase">Budget</label>
                    <p className="font-semibold">{selectedEnquiry.budget || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedEnquiry.message && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase">Message</label>
                  <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-[#FF6B35]">
                    <p className="text-[#4A4A4A] whitespace-pre-wrap">{selectedEnquiry.message}</p>
                  </div>
                </div>
              )}

              {/* Notes History */}
              {selectedEnquiry.notes && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase">Notes History</label>
                  <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800 whitespace-pre-wrap">
                    {selectedEnquiry.notes}
                  </div>
                </div>
              )}

              <hr className="border-gray-200" />

              {/* Status Update */}
              <div className="space-y-3">
                <label className="text-sm font-semibold text-[#1A1A1A]">Update Status</label>
                <div className="flex gap-3">
                  <Select value={modalStatus} onValueChange={setModalStatus}>
                    <SelectTrigger className="flex-1" data-testid="modal-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <config.icon className="w-4 h-4" />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Add a note (optional)..."
                  value={modalNotes}
                  onChange={(e) => setModalNotes(e.target.value)}
                  rows={2}
                  data-testid="modal-notes-input"
                />
              </div>

              {/* Email Response */}
              <div className="space-y-3">
                <label className="text-sm font-semibold text-[#1A1A1A]">Quick Email Response</label>
                <Textarea
                  placeholder="Type your email response here..."
                  value={emailResponse}
                  onChange={(e) => setEmailResponse(e.target.value)}
                  rows={4}
                  data-testid="email-response-input"
                />
                <Button 
                  variant="outline" 
                  onClick={handleSendEmail}
                  disabled={!emailResponse.trim()}
                  className="w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Open Email Client
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleModalStatusUpdate} 
              disabled={updatingStatus}
              className="bg-[#FF6B35] hover:bg-[#E85A2D]"
              data-testid="save-status-btn"
            >
              {updatingStatus ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
